import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useResendEmailMutation } from '../../../queries/auth';
import { emptyLocalStorage } from '../../../config/global';
import AuthShell, { WHATSAPP } from '../../../components/auth/AuthShell';
import { Banner, ErrorBanner, Head, Submit, withNode } from '../../../components/auth/parts';
import { classify } from '../../../components/auth/authErrors';
import { maskEmail } from '../../../components/auth/validators';
import useCountdown from '../../../components/auth/useCountdown';
import useAuthTitle from '../../../components/auth/useAuthTitle';

// The UI cap. The backend has its own, harder block (400 once attempts >= 4).
const MAX_RESENDS = 3;

const readAttempts = () => {
  const n = parseInt(localStorage.getItem('verification_attempts'), 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

function VerifyEmail () {
  const { t } = useTranslation();
  useAuthTitle('inbox');
  const S = (k, o) => t(`authPage.inbox.${k}`, o);

  const email = localStorage.getItem('email');
  const [attempts, setAttempts] = useState(readAttempts);
  const [banner, setBanner] = useState(null); // sent | limit | rate | offline | server
  const cd = useCountdown();
  const resendEmail = useResendEmailMutation({ inline: true });

  useEffect(() => { if (banner === 'rate' && cd.left === 0) setBanner(null); }, [banner, cd.left]);

  // Nothing to verify without the address the signup / login stored.
  if (!email || email === 'null' || email === 'undefined') return <Navigate to="/login" replace />;

  const limit = attempts >= MAX_RESENDS || banner === 'limit';
  const left = limit ? 0 : Math.max(0, MAX_RESENDS - attempts);

  const onResend = async () => {
    if (limit || resendEmail.isLoading || (banner === 'rate' && cd.left > 0)) return;
    setBanner(null);
    try {
      const data = await resendEmail.mutateAsync({ email });
      setAttempts(Number.isFinite(parseInt(data?.verification_attempts, 10)) ? parseInt(data.verification_attempts, 10) : attempts + 1);
      setBanner('sent');
    } catch (err) {
      const info = classify(err);
      if (info.status === 400) setBanner('limit'); // the backend's own block
      else if (info.kind === 'rate') { cd.start(info.retryAfter); setBanner('rate'); }
      else if (info.kind === 'offline') setBanner('offline');
      else setBanner('server');
    }
  };

  return (
    <AuthShell>
      <Head
        icon="mail" title={S('h')}
        text={withNode(S('p', { m: '{{m}}' }), <span className="ppa-mail">{maskEmail(email)}</span>)}
      />
      {banner === 'sent' && <Banner kind="good" icon="check" pair={t('authPage.inbox.sent', { returnObjects: true })} />}
      {limit && (
        <Banner
          kind="warn" icon="alert" pair={t('authPage.inbox.limit', { returnObjects: true })}
          extra={<div className="ppa-note-extra">WhatsApp <span className="ppa-num" dir="ltr">{WHATSAPP}</span></div>}
        />
      )}
      {(banner === 'rate' || banner === 'offline' || banner === 'server') && <ErrorBanner kind={banner} left={cd.left} />}
      <Submit type="button" outline id="resendV" busy={false} disabled={limit || resendEmail.isLoading} onClick={onResend}>{S('resend')}</Submit>
      <p className="ppa-hint tight">{S('left', { n: left })}</p>
      <p className="ppa-alt">
        {S('wrongMail')}{' '}
        <Link to="/signup" onClick={() => emptyLocalStorage()}>{S('wrongA')}</Link>
      </p>
    </AuthShell>
  );
}

export default VerifyEmail;
